import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Link, createFileRoute, useBlocker, useNavigate } from '@tanstack/react-router'
import { useEditorState, type Editor } from '@tiptap/react'
import { ArrowLeftIcon, CheckIcon, CloudOffIcon, EyeIcon, ImageIcon, PencilIcon, Redo2Icon, Settings2Icon, Trash2Icon, Undo2Icon } from 'lucide-react'
import { z } from 'zod'
import type { ArticleEntry, ArticlePatch, ContentMedia, ContentPreview, OrderView } from '@hedgehog/shared'
import { ApiRequestError, api } from '@/lib/api'
import { AppShell } from '@/components/app/app-shell'
import { ORDER_TABS, OrderError, startSession } from '@/components/app/order'
import { LiveFrame } from '@/components/app/live-frame'
import { ErrorState, FormError, LoadingState, errorMessage } from '@/components/app/states'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { ArticleBody, imageAttrs } from '@/components/articles/article-body'
import type { ArticleEditorHooks } from '@/components/articles/extensions'
import { ImageDialog } from '@/components/articles/image-dialog'
import { docToPt } from '@/components/articles/portable-text'
import { SLUG_RULE, articlePath, articleStatus, articlesApi, justCreated, siteUrl, slugify } from '@/components/articles/api'
import { useAutosave, type SaveState } from '@/components/articles/use-autosave'
import { cn } from '@/lib/utils'

// The article editor: title, body (Tiptap, saved as Portable Text), settings (address, excerpt,
// featured image, search engine description), autosave as a draft, preview in the site's own
// layout (the edit-mode frame of the page editor), publish, unpublish and delete.

// Full screen, outside the account layout (the `o_` prefix, like o_.$orderId.edit.tsx).
export const Route = createFileRoute('/o_/$orderId/articles/$postId')({
  validateSearch: z.object({ t: z.string().optional().catch(undefined) }),
  loaderDeps: ({ search }) => ({ t: search.t }),
  loader: async ({ params, deps, abortController }) => {
    if (deps.t) await startSession(params.orderId, deps.t, abortController.signal)
    const order = api<OrderView>(`/orders/${encodeURIComponent(params.orderId)}`, { signal: abortController.signal })
    if (params.postId === 'new') return { order: await order, entry: null }
    const created = justCreated.get(params.postId)
    if (created) {
      justCreated.delete(params.postId)
      return { order: await order, entry: created }
    }
    const [o, entry] = await Promise.all([order, articlesApi(params.orderId).get(params.postId, abortController.signal)])
    return { order: o, entry }
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData ? `${loaderData.entry?.title || 'New article'} · ${loaderData.order.businessName} · Hedgehog` : 'Article · Hedgehog' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
  pendingComponent: () => <LoadingState label="Opening the article" className="h-dvh" />,
  errorComponent: ({ error }) => (
    <AppShell>
      <OrderError error={error} title="We could not open this article" />
    </AppShell>
  ),
  component: ArticlePage,
})

const ARTICLES_TAB = ORDER_TABS.find((tab) => tab.to === '/o/$orderId/articles')!

function ArticlePage() {
  const { order } = Route.useLoaderData()
  const { t } = Route.useSearch()
  const navigate = useNavigate()

  // Drop the access token from the address bar once the session cookie is set.
  useEffect(() => {
    if (t) void navigate({ to: '.', search: { t: undefined }, replace: true })
  }, [t, navigate])

  if (!ARTICLES_TAB.available(order)) {
    return (
      <AppShell>
        <div className="flex max-w-xl flex-col items-start gap-3 py-8">
          <h1 className="text-xl font-semibold">Articles are not available</h1>
          <p className="text-muted-foreground">{ARTICLES_TAB.opensWhen}</p>
          <Link to="/o/$orderId" params={{ orderId: order.id }} className={buttonVariants({ variant: 'outline' })}>
            Back to your account
          </Link>
        </div>
      </AppShell>
    )
  }
  return <ArticleEditor order={order} />
}

type Field = 'title' | 'content' | 'excerpt' | 'seo' | 'featured' | 'social'
const FIELD_LABEL: Record<Field, string> = { title: 'Title', content: 'Text', excerpt: 'Excerpt', seo: 'Search engine description', featured: 'Featured image', social: 'Social image' }
type Featured = ArticleEntry['featuredImage']
type Meta = Pick<ArticleEntry, 'slug' | 'liveSlug' | 'status' | 'hasDraft' | 'publishedAt'>
const metaOf = (r: ArticleEntry): Meta => ({ slug: r.slug, liveSlug: r.liveSlug, status: r.status, hasDraft: r.hasDraft, publishedAt: r.publishedAt })

/** The slug still follows the title: never published and equal to the title's slug (or a numbered variant). */
function followsTitle(e: ArticleEntry): boolean {
  if (e.publishedAt || e.status !== 'draft') return false
  const base = slugify(e.title) || 'article'
  return e.slug === base || new RegExp(`^${base.slice(0, 95)}-\\d+$`).test(e.slug) || !e.slug
}

/** Display URL of a featured image value (media library file or stored src). */
function featuredSrc(origin: string | null, v: Featured): string | null {
  if (!v) return null
  const o = v as { src?: unknown; meta?: { storageKey?: unknown } }
  if (typeof o.src === 'string') return siteUrl(origin, o.src)
  return typeof o.meta?.storageKey === 'string' ? siteUrl(origin, `/_emdash/api/media/file/${o.meta.storageKey}`) : null
}

function ArticleEditor({ order }: { order: OrderView }) {
  const { entry } = Route.useLoaderData()
  const { orderId } = Route.useParams()
  const navigate = useNavigate()
  const a = useMemo(() => articlesApi(orderId), [orderId])
  // The loader runs again when a new article gets its id; the editor keeps its own state.
  const [initial] = useState(entry)
  const idRef = useRef<string | null>(initial?.id ?? null)
  // The version the editor's saves build on: sent with each save, so a change made elsewhere since is not overwritten.
  const revRef = useRef<string | null>(initial?.rev ?? null)
  // The content as the site stores it: blocks the writer did not touch are sent back unchanged.
  const storedContent = useRef<unknown[]>(initial?.content ?? [])

  const [title, setTitle] = useState(initial?.title ?? '')
  const [excerpt, setExcerpt] = useState(initial?.excerpt ?? '')
  const [seo, setSeo] = useState(initial?.seo?.description ?? '')
  const [seoEnabled, setSeoEnabled] = useState<boolean | null>(initial ? initial.seo !== null : null)
  const [featured, setFeatured] = useState<Featured>(initial?.featuredImage ?? null)
  // Social image (EmDash SEO image): `src` shows it; `value` is a change waiting to be saved (null removes it).
  const [social, setSocial] = useState<{ src: string | null; value?: ContentMedia['value'] | null }>({ src: initial?.seo?.image ?? null })
  const [meta, setMeta] = useState<Meta>(initial ? metaOf(initial) : { slug: '', liveSlug: '', status: 'draft', hasDraft: false, publishedAt: null })
  const [slugAuto, setSlugAuto] = useState(initial ? followsTitle(initial) : true)
  const values = useRef({ title, excerpt, seo, featured, social, slugAuto, seoEnabled, meta })
  values.current = { title, excerpt, seo, featured, social, slugAuto, seoEnabled, meta }

  const [editor, setEditor] = useState<Editor | null>(null)
  const [words, setWords] = useState(0)
  const [origin, setOrigin] = useState<string | null>(null)
  const [originError, setOriginError] = useState<unknown>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState<null | 'publishing' | 'unpublishing' | 'deleting' | 'preview'>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [preview, setPreview] = useState<ContentPreview | null>(null)
  const [imageFor, setImageFor] = useState<'insert' | 'featured' | 'social' | null>(null)
  const insertAt = useRef<number | null>(null)
  const editorRef = useRef<Editor | null>(null)
  const titleId = useId()

  const applyMeta = (r: ArticleEntry) => {
    // values.current too: publish and preview read it right after a save, before the next render.
    values.current.meta = metaOf(r)
    revRef.current = r.rev
    storedContent.current = r.content
    setMeta(metaOf(r))
    setSeoEnabled(r.seo !== null)
  }

  const save = async (fields: ReadonlySet<Field>) => {
    const v = values.current
    const patch: ArticlePatch = {}
    if (fields.has('title') && v.title.trim()) patch.title = v.title.trim()
    if (fields.has('title') && v.slugAuto && patch.title) patch.slugAuto = true
    if (fields.has('content') && editorRef.current) patch.content = docToPt(editorRef.current.getJSON(), storedContent.current)
    if (fields.has('excerpt')) patch.excerpt = v.excerpt
    if (fields.has('featured')) patch.featured_image = v.featured
    if (fields.has('seo') && v.seoEnabled) patch.seo = { description: v.seo.trim() || null }
    if (fields.has('social') && v.seoEnabled && v.social.value !== undefined) patch.seo = { ...patch.seo, image: v.social.value }
    if (!Object.keys(patch).length) return
    if (revRef.current) patch.rev = revRef.current
    if (!idRef.current) {
      const r = await a.create(patch)
      idRef.current = r.id
      justCreated.set(r.id, r)
      applyMeta(r)
      void navigate({ to: '/o/$orderId/articles/$postId', params: { orderId, postId: r.id }, replace: true })
      return
    }
    applyMeta(await a.put(idRef.current, patch))
  }
  const autosave = useAutosave<Field>(save, (f) => FIELD_LABEL[f])

  // Site origin: images are stored as site paths, and the preview frame needs it.
  useEffect(() => {
    const ctl = new AbortController()
    a.preview('/', ctl.signal)
      .then((p) => setOrigin(p.origin))
      .catch((e) => !ctl.signal.aborted && setOriginError(e))
    return () => ctl.abort()
  }, [a])

  // Leaving with unsaved changes: save first, and ask only if that fails.
  useBlocker({
    shouldBlockFn: async ({ next }) => {
      if (!autosave.pending()) return false
      if ((next.params as { postId?: string }).postId === idRef.current) return false
      if (await autosave.flush()) return false
      return !window.confirm('Your latest changes are not saved yet. Leave anyway?')
    },
    enableBeforeUnload: () => autosave.pending(),
  })

  useEffect(() => {
    if (!notice) return
    const t = setTimeout(() => setNotice(null), 6000)
    return () => clearTimeout(t)
  }, [notice])

  const imageSrc = (asset: { _ref?: string; url?: string } | null) => {
    if (!asset) return ''
    if (typeof asset.url === 'string' && asset.url) return siteUrl(origin, asset.url)
    return asset._ref ? siteUrl(origin, `/_emdash/api/media/file/${asset._ref}`) : ''
  }
  const hooks = useRef<ArticleEditorHooks>({ imageSrc, onImage: () => {} })
  hooks.current = {
    imageSrc,
    onImage: () => {
      insertAt.current = editorRef.current?.state.selection.from ?? null
      setImageFor('insert')
    },
  }

  const upload = async (file: File, alt: string): Promise<ContentMedia> => a.upload(file, alt)

  const flushOrFail = async (what: string): Promise<boolean> => {
    if (!(await autosave.flush())) {
      setActionError(`Your latest changes are not saved yet, so the article was not ${what}. Try again in a moment.`)
      return false
    }
    if (!idRef.current) {
      setActionError('Write a title or some text first.')
      return false
    }
    return true
  }

  const publish = async () => {
    setActionError(null)
    if (!values.current.title.trim()) return setActionError('Add a title before you publish.')
    setBusy('publishing')
    try {
      if (!(await flushOrFail('published'))) return
      applyMeta(await a.publish(idRef.current!))
      setNotice('Published. Visitors see it on your blog within a minute.')
    } catch (e) {
      setActionError(`Not published: ${errorMessage(e)}`)
    } finally {
      setBusy(null)
    }
  }

  const unpublish = async () => {
    setActionError(null)
    setBusy('unpublishing')
    try {
      if (!(await flushOrFail('unpublished'))) return
      applyMeta(await a.unpublish(idRef.current!))
      setNotice('Unpublished. The article is a draft again and visitors no longer see it.')
    } catch (e) {
      setActionError(`Not unpublished: ${errorMessage(e)}`)
    } finally {
      setBusy(null)
    }
  }

  const remove = async () => {
    setActionError(null)
    setBusy('deleting')
    try {
      // No save may land after the delete; saving goes on if the delete fails.
      await autosave.flush()
      if (idRef.current) await a.remove(idRef.current)
      autosave.stop()
      await navigate({ to: '/o/$orderId/articles', params: { orderId }, replace: true })
    } catch (e) {
      setActionError(`Not deleted: ${errorMessage(e)}`)
      setBusy(null)
    }
  }

  const togglePreview = async () => {
    if (preview) return setPreview(null)
    setActionError(null)
    setBusy('preview')
    try {
      if (!(await flushOrFail('previewed'))) return
      // Edit mode shows the draft at the address the site serves now; a new address applies on publish.
      setPreview(await a.preview(articlePath(values.current.meta.liveSlug || values.current.meta.slug)))
    } catch (e) {
      setActionError(`The preview did not open: ${errorMessage(e)}`)
    } finally {
      setBusy(null)
    }
  }

  const onImageSubmit = async (file: File | null, alt: string, caption: string): Promise<string | null> => {
    try {
      if (imageFor === 'insert') {
        if (!file) return 'Choose an image first.'
        const m = await upload(file, alt)
        const e = editorRef.current
        if (e) {
          const at = Math.min(insertAt.current ?? e.state.selection.from, e.state.doc.content.size)
          e.chain().insertContentAt(at, { type: 'image', attrs: imageAttrs(m, alt, caption) }).run()
        }
      } else if (imageFor === 'social') {
        if (!file) return 'Choose an image first.'
        const m = await upload(file, alt)
        const next = { src: m.url, value: { ...m.value, alt } }
        setSocial(next)
        values.current.social = next
        autosave.mark('social')
      } else {
        const next = file ? { ...(await upload(file, alt)).value, alt } : featured ? { ...featured, alt } : null
        setFeatured(next)
        values.current.featured = next
        autosave.mark('featured')
      }
      return null
    } catch (e) {
      return errorMessage(e)
    }
  }

  const back = (
    <Link to="/o/$orderId/articles" params={{ orderId }} className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2 text-sm font-medium outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50">
      <ArrowLeftIcon className="size-4" aria-hidden />
      Articles
    </Link>
  )

  if (originError) {
    const notReady = originError instanceof ApiRequestError && originError.code === 'not_ready'
    return (
      <div className="flex min-h-dvh flex-col items-start gap-4 p-6">
        {back}
        <main id="main" className="w-full max-w-xl">
          {notReady ? <p className="rounded-xl border border-border p-4 text-sm">{errorMessage(originError)}</p> : <ErrorState error={originError} title="We could not reach your site" />}
        </main>
      </div>
    )
  }
  if (!origin) return <LoadingState label="Opening the article" className="h-dvh" />

  const status = articleStatus(meta)
  const published = meta.status === 'published'
  const unpublished = published && (meta.hasDraft || autosave.pending())
  const publicHost = order.site?.hostname ? `https://${order.site.hostname}` : origin

  return (
    <div className="flex h-dvh flex-col bg-background">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-background focus:px-3 focus:py-2">
        Skip to the article
      </a>
      <header className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border bg-background px-2 py-2 sm:flex-nowrap sm:px-3">
        {back}
        <span className="hidden h-5 w-px bg-border sm:block" aria-hidden />
        <Badge variant="secondary" className="h-6 px-2">
          {status.label}
        </Badge>
        <SaveIndicator state={autosave.state} onRetry={() => void autosave.flush()} />
        <span className="flex-1" />
        <span className="hidden text-sm text-muted-foreground tabular-nums sm:inline" aria-live="off">
          {words} {words === 1 ? 'word' : 'words'}
        </span>
        {editor && !preview && <UndoRedo editor={editor} />}
        <Button variant="outline" size="sm" onClick={togglePreview} disabled={busy === 'preview'} aria-pressed={!!preview}>
          {busy === 'preview' ? <Spinner /> : preview ? <PencilIcon aria-hidden /> : <EyeIcon aria-hidden />}
          {preview ? 'Edit' : 'Preview'}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
          <Settings2Icon aria-hidden />
          Settings
        </Button>
        <Button size="sm" onClick={publish} disabled={!!busy || (published && !unpublished)}>
          {busy === 'publishing' && <Spinner />}
          {!published ? 'Publish' : unpublished ? 'Publish changes' : 'Published'}
        </Button>
      </header>

      <main id="main" aria-labelledby={titleId} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 pt-3">
        <div role="status" aria-live="polite" className="mx-auto w-full max-w-[720px] empty:hidden">
          {notice && <p className="rounded-lg bg-success/10 px-3 py-2 text-sm text-success">{notice}</p>}
        </div>
        {actionError && (
          <div className="mx-auto w-full max-w-[720px]">
            <FormError message={actionError} />
          </div>
        )}
        {autosave.state.kind === 'conflict' && (
          <div role="alert" className="mx-auto flex w-full max-w-[720px] flex-wrap items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
            <p className="flex-1">
              This article was changed somewhere else (another tab or the site admin) since you opened it, so your latest changes are not saved. Copy any text you want to keep, then
              reload to get the latest version.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                autosave.stop()
                window.location.reload()
              }}
            >
              Reload
            </Button>
          </div>
        )}

        {preview && (
          <LiveFrame
            key={preview.url}
            src={preview.url}
            width={1280}
            title="Preview of the article on your site"
            heightClass="mb-3 min-h-96 flex-1"
            fallback={<p className="rounded-xl border border-border p-4 text-sm">The preview did not load. Go back to editing and try again.</p>}
          />
        )}

        <div className={cn('mx-auto w-full max-w-[720px] pt-6 pb-24', preview && 'hidden')}>
          {featured && (
            <img src={featuredSrc(origin, featured) ?? ''} alt="" className="mb-8 aspect-[2/1] w-full rounded-xl object-cover" />
          )}
          <h2 id={titleId} className="sr-only">
            {title || 'New article'}
          </h2>
          <textarea
            className="hh-title mb-6 w-full bg-transparent text-4xl leading-tight font-semibold tracking-tight outline-none placeholder:text-muted-foreground/60 sm:text-[2.6rem]"
            rows={1}
            value={title}
            placeholder="Article title"
            aria-label="Title"
            maxLength={300}
            onChange={(e) => {
              const v = e.target.value.replace(/\s*\n\s*/g, ' ')
              setTitle(v)
              values.current.title = v
              autosave.mark('title')
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || (e.key === 'ArrowDown' && e.currentTarget.selectionStart === e.currentTarget.value.length)) {
                e.preventDefault()
                const ed = editorRef.current
                if (!ed) return
                ed.commands.focus('start')
                // Tiptap focuses on the next frame; focus now so the next keystrokes land in the text.
                ed.view.focus()
              }
            }}
          />
          <ArticleBody
            initial={initial?.content ?? []}
            hooks={hooks}
            upload={(f) =>
              upload(f, '').catch((e: unknown) => {
                setNotice(null)
                setActionError(`The image was not added: ${errorMessage(e)}`)
                return null
              })
            }
            onReady={(e) => {
              editorRef.current = e
              setEditor(e)
              setWords(e.storage.characterCount.words())
              if (!initial) requestAnimationFrame(() => document.querySelector<HTMLTextAreaElement>('.hh-title')?.focus())
            }}
            onChange={() => {
              if (editorRef.current) setWords(editorRef.current.storage.characterCount.words())
              autosave.mark('content')
            }}
            onNotice={setNotice}
          />
        </div>
      </main>

      <ImageDialog
        open={imageFor !== null}
        title={imageFor === 'featured' ? 'Featured image' : imageFor === 'social' ? 'Social image' : 'Add an image'}
        description={
          imageFor === 'featured' ? 'Shown at the top of the article and in link previews.' : imageFor === 'social' ? 'Shown when the article is shared, instead of the featured image. 1200 × 630 pixels works everywhere.' : 'Upload an image to your site and describe it.'
        }
        withCaption={imageFor === 'insert'}
        currentSrc={imageFor === 'featured' ? featuredSrc(origin, featured) : imageFor === 'social' && social.src ? (social.src.startsWith('/') ? origin + social.src : social.src) : null}
        currentAlt={imageFor === 'featured' ? String((featured as { alt?: unknown } | null)?.alt ?? '') : ''}
        onClose={() => {
          setImageFor(null)
          if (imageFor === 'insert') editorRef.current?.view.focus()
        }}
        onSubmit={onImageSubmit}
      />

      <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Article settings</SheetTitle>
            <SheetDescription>Saved automatically, like the article.</SheetDescription>
          </SheetHeader>
          <div className="flex flex-col gap-6 px-4 pb-6">
            <SlugField
              key={meta.slug}
              slug={meta.slug}
              auto={slugAuto}
              published={!!meta.publishedAt || published}
              staged={!!meta.liveSlug && meta.slug !== meta.liveSlug}
              prefix={publicHost.replace(/^https?:\/\//, '')}
              disabled={!idRef.current}
              onSave={async (slug) => {
                if (!(await autosave.flush()) || !idRef.current) return 'Your latest changes are not saved yet. Try again in a moment.'
                try {
                  applyMeta(await a.put(idRef.current, { slug, ...(revRef.current ? { rev: revRef.current } : {}) }))
                  setSlugAuto(false)
                  return null
                } catch (e) {
                  return errorMessage(e)
                }
              }}
            />

            <div className="flex flex-col gap-2">
              <Label htmlFor={`${titleId}-x`}>Excerpt</Label>
              <Textarea
                id={`${titleId}-x`}
                value={excerpt}
                maxLength={1000}
                rows={3}
                aria-describedby={`${titleId}-xh`}
                onChange={(e) => {
                  setExcerpt(e.target.value)
                  values.current.excerpt = e.target.value
                  autosave.mark('excerpt')
                }}
              />
              <p id={`${titleId}-xh`} className="text-xs text-muted-foreground">
                A sentence or two shown in your blog list{seoEnabled === false ? ' and in search results' : ''}.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">Featured image</span>
              {featured ? (
                <div className="flex flex-col gap-2">
                  <img src={featuredSrc(origin, featured) ?? ''} alt={String((featured as { alt?: unknown }).alt ?? '')} className="aspect-video w-full rounded-lg border border-border object-cover" />
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setImageFor('featured')}>
                      Replace or describe
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFeatured(null)
                        values.current.featured = null
                        autosave.mark('featured')
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" size="sm" className="w-fit" onClick={() => setImageFor('featured')}>
                  <ImageIcon aria-hidden />
                  Add a featured image
                </Button>
              )}
            </div>

            {seoEnabled && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">Social image</span>
                {social.src ? (
                  <div className="flex flex-col gap-2">
                    <img src={social.src.startsWith('/') ? origin + social.src : social.src} alt="" className="aspect-[1.91/1] w-full rounded-lg border border-border object-cover" />
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setImageFor('social')}>
                        Replace
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const next = { src: null, value: null }
                          setSocial(next)
                          values.current.social = next
                          autosave.mark('social')
                        }}
                      >
                        Use the featured image
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" className="w-fit" onClick={() => setImageFor('social')}>
                    <ImageIcon aria-hidden />
                    Choose a social image
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">Shown when the article is shared on social networks and in messages. Without one, the featured image is shown.</p>
              </div>
            )}

            {seoEnabled && (
              <div className="flex flex-col gap-2">
                <Label htmlFor={`${titleId}-s`}>Search engine description</Label>
                <Textarea
                  id={`${titleId}-s`}
                  value={seo}
                  maxLength={500}
                  rows={3}
                  aria-describedby={`${titleId}-sh`}
                  onChange={(e) => {
                    setSeo(e.target.value)
                    values.current.seo = e.target.value
                    autosave.mark('seo')
                  }}
                />
                <p id={`${titleId}-sh`} className="text-xs text-muted-foreground">
                  <span className={cn('tabular-nums', seo.length > 160 && 'text-warning')}>{seo.length}/160</span> · Shown under the title in search results. Empty: the excerpt is used.
                </p>
              </div>
            )}

            <div className="flex flex-col gap-2 border-t border-border pt-4">
              {published && (
                <Button variant="outline" size="sm" className="w-fit" onClick={unpublish} disabled={!!busy}>
                  {busy === 'unpublishing' && <Spinner />}
                  Unpublish
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger render={<Button variant="destructive" size="sm" className="w-fit" disabled={!!busy} />}>
                  <Trash2Icon aria-hidden />
                  Delete article
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this article?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {published ? 'It is removed from your site and your blog page. ' : ''}You cannot undo this here.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction variant="destructive" onClick={remove} disabled={busy === 'deleting'}>
                      {busy === 'deleting' && <Spinner />}
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

function SaveIndicator({ state, onRetry }: { state: SaveState; onRetry: () => void }) {
  return (
    <span role="status" aria-live="polite" className="flex items-center gap-1.5 text-sm text-muted-foreground">
      {state.kind === 'saving' && (
        <>
          <Spinner className="size-3.5" aria-hidden />
          Saving…
        </>
      )}
      {state.kind === 'saved' && (
        <>
          <CheckIcon className="size-3.5" aria-hidden />
          Saved
        </>
      )}
      {(state.kind === 'invalid' || state.kind === 'conflict') && (
        <>
          <CloudOffIcon className="size-3.5 text-destructive" aria-hidden />
          <span className="text-destructive">{state.kind === 'conflict' ? 'Not saved: changed elsewhere' : `Not saved: ${state.message}`}</span>
        </>
      )}
      {state.kind === 'error' && (
        <>
          <CloudOffIcon className="size-3.5 text-destructive" aria-hidden />
          <span className="text-destructive" title={state.message}>
            Not saved{state.retrying ? ', retrying' : `: ${state.message}`}
          </span>
          <Button variant="link" size="sm" className="h-auto px-1" onClick={onRetry}>
            Retry now
          </Button>
        </>
      )}
    </span>
  )
}

function UndoRedo({ editor }: { editor: Editor }) {
  const s = useEditorState({ editor, selector: ({ editor: e }) => ({ undo: e.can().undo(), redo: e.can().redo() }) })
  return (
    <span className="flex">
      <Button variant="ghost" size="icon-sm" aria-label="Undo (⌘Z)" title="Undo ⌘Z" disabled={!s.undo} onClick={() => editor.chain().undo().run() && editor.view.focus()}>
        <Undo2Icon aria-hidden />
      </Button>
      <Button variant="ghost" size="icon-sm" aria-label="Redo (⇧⌘Z)" title="Redo ⇧⌘Z" disabled={!s.redo} onClick={() => editor.chain().redo().run() && editor.view.focus()}>
        <Redo2Icon aria-hidden />
      </Button>
    </span>
  )
}

function SlugField({
  slug,
  auto,
  published,
  staged,
  prefix,
  disabled,
  onSave,
}: {
  slug: string
  auto: boolean
  published: boolean
  /** A new address waits for the next publish. */
  staged: boolean
  prefix: string
  disabled: boolean
  onSave: (slug: string) => Promise<string | null>
}) {
  const id = useId()
  const [value, setValue] = useState(slug)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const commit = async () => {
    const v = value.trim().toLowerCase()
    if (v === slug) return setError(null)
    if (!SLUG_RULE.test(v) || v.length > 100) return setError('Use lowercase letters, digits and single hyphens, for example spring-opening-hours.')
    setSaving(true)
    setError(await onSave(v))
    setSaving(false)
  }

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>Address</Label>
      <div className="flex items-center gap-1 text-sm">
        <span className="max-w-40 truncate text-muted-foreground" title={prefix}>
          {prefix}/
        </span>
        <Input
          id={id}
          value={value}
          disabled={disabled || saving}
          maxLength={100}
          aria-invalid={!!error}
          aria-describedby={`${id}-h${error ? ` ${id}-e` : ''}`}
          onChange={(e) => {
            setValue(e.target.value)
            setError(null)
          }}
          onBlur={() => void commit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void commit()
            }
          }}
        />
        <span className="text-muted-foreground">/</span>
        {saving && <Spinner aria-label="Saving the address" />}
      </div>
      <p id={`${id}-h`} className="text-xs text-muted-foreground">
        {disabled
          ? 'Set once the article is saved.'
          : published
            ? `Changing the address of a published article breaks links to it.${staged ? ' The new address applies when you publish the changes.' : ''}`
            : auto
              ? 'Follows the title until you change it.'
              : 'The article is at this address once you publish it.'}
      </p>
      {error && (
        <p id={`${id}-e`} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}
